import { useRef } from 'react'
import { useModalDialog } from '../../../hooks'
import { CandidateBulkForm, CandidateBulkEditForm, CandidateEditForm, CandidateMilestoneForm, InterviewRescheduleForm } from '../forms'
import { formatSelectOptions } from '../../../components'

export const useCandidateDialog = () => {

  const { showModalDialog } = useModalDialog()
  const bulkRef = useRef()
  const editRef = useRef()
  const milestoneRef = useRef()
  const editMilestoneRef = useRef()
  const bulkEditRef = useRef()
  const rescheduleRef = useRef()

  const showBulkAddCandidateDialog = (onSubmit) => {

    const config = {
      title: "Lägg till flera kandidater",
      Content: CandidateBulkForm,
      contentProps: {
        submitForm: onSubmit,
        ref: bulkRef,
      },
      onSubmit: () => bulkRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
      submitLabel: "Spara",
      submitLabelLoading: "Sparar...",
    }

    showModalDialog(config)
  }

  const showEditCandidateDialog = (data, onSubmit) => {

    const config = {
      title: "Redigera kandidat",
      Content: CandidateEditForm,
      contentProps: {
        data: data,
        submitForm: onSubmit,
        ref: editRef,
      },
      onSubmit: () => editRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
      submitLabel: "Spara",
      submitLabelLoading: "Sparar...",
    }

    showModalDialog(config)
  }

  const showAddMilestoneDialog = (candidates, onSubmit) => {

    const config = {
      title: candidates.length > 1 ? `Ny händelse (${candidates.length} kandidater)` : "Ny händelse",
      Content: CandidateMilestoneForm,
      contentProps: {
        data: candidates,
        submitForm: onSubmit,
        ref: milestoneRef,
      },
      onSubmit: () => milestoneRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
      submitLabel: "Spara",
      submitLabelLoading: "Sparar...",
    }

    showModalDialog(config)
  }

  // Move an existing booking. Separate from the milestone dialog on purpose: rescheduling
  // isn't a pipeline event, so it must not log a second interview milestone.
  const showRescheduleInterviewDialog = (interview, onSubmit) => {

    const config = {
      title: "Boka om intervju",
      Content: InterviewRescheduleForm,
      contentProps: {
        data: [interview],
        submitForm: onSubmit,
        ref: rescheduleRef,
      },
      onSubmit: () => rescheduleRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
      submitLabel: "Spara",
      submitLabelLoading: "Sparar...",
    }

    showModalDialog(config)
  }

  // Cancel a booking. Names what actually goes away (the time and the signups) and what
  // stays (the pipeline stage), because "Avboka" alone reads like it might undo the stage.
  const showCancelInterviewDialog = (interview, onSubmit) => {

    const config = {
      title: "Avboka intervju",
      Content: () => <div>
          <div>Avboka Intervju {interview.interview_round} med {interview.candidate_name}?</div>
          <ul>
            <li>Tiden tas bort från kalendern och anmälda kollegor avanmäls.</li>
            {/* Deliberately doesn't name the stage: the round being cancelled is not
                necessarily the candidate's current one (you can cancel Intervju 1 for
                someone already on Intervju 2). */}
            <li>Skedet i pipelinen ändras inte, och händelsen står kvar i historiken.</li>
          </ul>
        </div>,
      onSubmit: () => onSubmit(interview),
      submitLabelLoading: "Avbokar...",
    }

    showModalDialog(config)
  }

  const showReassignOwnerDialog = (candidates, users, onSubmit) => {

    const config = {
      title: "Byt ägare",
      Content: CandidateBulkEditForm,
      contentProps: {
        field: "owner_user_id",
        label: "Ny ägare",
        type: "select",
        options: formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' }),
        data: candidates,
        submitForm: onSubmit,
        ref: bulkEditRef,
      },
      onSubmit: () => bulkEditRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
      submitLabel: "Spara",
      submitLabelLoading: "Sparar...",
    }

    showModalDialog(config)
  }

  const showUpdateSourceDialog = (candidates, onSubmit) => {

    const config = {
      title: "Uppdatera källa",
      Content: CandidateBulkEditForm,
      contentProps: {
        field: "source",
        label: "Ny källa",
        type: "text",
        data: candidates,
        submitForm: onSubmit,
        ref: bulkEditRef,
      },
      onSubmit: () => bulkEditRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
      submitLabel: "Spara",
      submitLabelLoading: "Sparar...",
    }

    showModalDialog(config)
  }

  const showEditMilestoneDialog = (milestone, onSubmit) => {

    const config = {
      title: "Redigera händelse",
      Content: CandidateMilestoneForm,
      contentProps: {
        mode: "edit",
        data: [milestone],
        submitForm: onSubmit,
        ref: editMilestoneRef,
      },
      onSubmit: () => editMilestoneRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
      submitLabel: "Spara",
      submitLabelLoading: "Sparar...",
    }

    showModalDialog(config)
  }

  const showDeleteMilestoneDialog = (milestone, onSubmit) => {

    const config = {
      title: "Ta bort händelse",
      Content: () => <div>Är du säker på att du vill ta bort denna händelse?</div>,
      onSubmit: () => onSubmit(milestone),
      submitLabelLoading: "Tar bort...",
    }

    showModalDialog(config)
  }

  const showDeleteCandidateDialog = (data, onSubmit) => {

    const config = {
      title: "Ta bort kandidat",
      Content: () => <div>
          <div>Är du säker på att du vill ta bort följande?</div>
          <ul>
            {data.length > 0 &&
              <li>{data.length + ' kandidat' + (data.length > 1 ? 'er' : '')} tas bort permanent, tillsammans med hela historiken.</li>
            }
          </ul>
        </div>,
      onSubmit: () => onSubmit(data),
      submitLabelLoading: "Tar bort...",
    }

    showModalDialog(config)
  }

  return {
    showBulkAddCandidateDialog,
    showEditCandidateDialog,
    showAddMilestoneDialog,
    showEditMilestoneDialog,
    showRescheduleInterviewDialog,
    showCancelInterviewDialog,
    showDeleteMilestoneDialog,
    showDeleteCandidateDialog,
    showReassignOwnerDialog,
    showUpdateSourceDialog,
  }
}
